/**
 * 브라우저 저장소 안전 접근 헬퍼 (WEB-R).
 *
 * iPad Safari·사생활 모드 등 저장소가 차단된 환경에서는 getItem/setItem/removeItem
 * 호출 자체가 throw하므로, 화면 렌더 경로의 직접 접근을 여기로 모은다.
 * 읽기 실패는 null(호출부가 안전한 기본값으로 대체), 쓰기 실패는 무시(기억 기능 없이 동작 계속).
 *
 * 계약: 이 모듈의 헬퍼가 SSR(`typeof window === 'undefined'`)과 저장소 throw를
 * 전부 책임진다. 호출부에는 SSR 가드나 try/catch를 중복으로 두지 않는다.
 * 단, 저장소 외의 window API(dispatchEvent 등)를 함께 쓰는 호출부는 그 API용
 * 가드를 별도로 둔다.
 */

const isBrowser = () => typeof window !== 'undefined';

/** 저장된 문자열을 읽는다. SSR·차단 환경에서는 null을 반환한다. */
export const readStoredValue = (key: string): string | null => {
  if (!isBrowser()) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

/** 문자열을 저장한다. 성공 여부를 반환하며 실패해도 throw하지 않는다. */
export const writeStoredValue = (key: string, value: string): boolean => {
  if (!isBrowser()) return false;
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

/** 저장된 값을 지운다. 실패해도 throw하지 않는다. */
export const removeStoredValue = (key: string): void => {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // 저장소가 차단된 환경에서는 지울 값도 없으므로 무시한다.
  }
};

/** 세션 저장소에서 문자열을 읽는다. SSR·차단 환경에서는 null을 반환한다. */
export const readSessionStoredValue = (key: string): string | null => {
  if (!isBrowser()) return null;
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

/** 세션 저장소에 문자열을 저장한다. 성공 여부를 반환하며 실패해도 throw하지 않는다. */
export const writeSessionStoredValue = (key: string, value: string): boolean => {
  if (!isBrowser()) return false;
  try {
    window.sessionStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

/** 세션 저장소에서 값을 지운다. 실패해도 throw하지 않는다. */
export const removeSessionStoredValue = (key: string): void => {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // 저장소가 차단된 환경에서는 지울 값도 없으므로 무시한다.
  }
};
